const inputCls = "w-20 rounded-md border border-slate-300 bg-white px-2 py-1 text-right font-mono-tech text-sm text-slate-900 outline-none focus:border-[#C8102E] focus:ring-2 focus:ring-[#C8102E]/20";
const EDU = { none: "Less than secondary", secondary: "Secondary", one_year: "1-year post-sec.", two_year: "2-year post-sec.", bachelor: "Bachelor's", two_or_more: "Two+ credentials", masters: "Master's", doctoral: "Doctoral" };
const TEER = { 0: "TEER 0", 1: "TEER 1", 2: "TEER 2", 3: "TEER 3", 4: "TEER 4", 5: "TEER 5" };
const LABELS = {
  legalStatus: { work_permit: "Valid work permit (incl. PGWP)", study_permit: "Valid study permit", none: "No permit" },
  jobOfferRegion: { northern: "Northern Ontario", eastern: "Eastern Ontario", central: "Central Ontario (non-GTA)", southwestern: "Southwestern Ontario", gta: "GTA (except Toronto)", toronto: "Toronto", "": "Not specified" },
  languagesKnown: { 0: "No official language", 1: "One official language", 2: "Two official languages" },
  canadianCredentials: { 0: "No Canadian credential", 1: "One Canadian credential", 2: "Two+ Canadian credentials" },
  nocBroad: { 0: "0 – Legislative & senior management", 1: "1 – Business, finance & admin", 2: "2 – Natural & applied sciences", 3: "3 – Health", 4: "4 – Education, law, social & government", 5: "5 – Art, culture, recreation & sport", 6: "6 – Sales & service", 7: "7 – Trades, transport & equipment", 8: "8 – Natural resources & agriculture", 9: "9 – Manufacturing & utilities" },
};
const UNITS = { minCLB: "CLB", bestCLB: "CLB", age: "age", jobOfferWage: "$/hr", jobOfferMonths: "mo", ontarioFallbackMonths: "mo", canadianEarnings: "$" };
const MONEY = new Set(["canadianEarnings", "jobOfferWage"]);

function fmtNum(n, src) {
  if (src === "canadianEarnings") return `$${Number(n).toLocaleString()}`;
  if (src === "jobOfferWage") return `$${n}`;
  return n;
}
function bandLabel(b, src) {
  const prefix = MONEY.has(src) ? "" : `${UNITS[src] || "yr"} `;
  const suffix = src === "jobOfferWage" ? "/hr" : "";
  if (b.max == null) return `${prefix}${fmtNum(b.min, src)}+${suffix}`;
  if (b.min === b.max) return `${prefix}${fmtNum(b.min, src)}${suffix}`;
  return `${prefix}${fmtNum(b.min, src)}–${fmtNum(b.max, src)}${suffix}`;
}
function mapLabel(k, src) {
  if (src === "eduLevel") return EDU[k] || k;
  if (src === "jobOfferTeerKey") return TEER[k] || k;
  return LABELS[src]?.[k] ?? k;
}
function factorMax(f) {
  if (f.exclude_from_max) return 0;
  if (f.type === "boolean") return f.points;
  if (f.type === "map") return Math.max(...Object.values(f.map));
  return Math.max(...f.bands.map((b) => b.points));
}

export function GridsEditor({ grids, original, onChange }) {
  const setFactor = (gid, fid, mut) =>
    onChange({
      ...grids,
      [gid]: { ...grids[gid], factors: grids[gid].factors.map((f) => (f.id === fid ? mut({ ...f }) : f)) },
    });
  const num = (v) => (v === "" ? 0 : Math.max(0, Number(v)));
  const dirty = (gid, fid, cur) => JSON.stringify(cur) !== JSON.stringify(original[gid].factors.find((f) => f.id === fid));

  return (
    <div className="grid gap-6 xl:grid-cols-2" data-testid="admin-grids-editor">
      {Object.entries(grids).map(([gid, g]) => (
        <div key={gid} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`admin-grid-${gid}`}>
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-heading text-base font-semibold text-slate-900">{g.label}</h3>
            <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 font-mono-tech text-xs font-semibold text-slate-600" data-testid={`admin-grid-max-${gid}`}>
              max {g.factors.reduce((t, f) => t + factorMax(f), 0)}
            </span>
          </div>
          <div className="mt-4 space-y-5">
            {g.factors.map((f) => (
              <div key={f.id} className={`rounded-lg border p-3 ${dirty(gid, f.id, f) ? "border-amber-300 bg-amber-50/50" : "border-slate-100"}`} data-testid={`admin-factor-${gid}-${f.id}`}>
                <p className="text-sm font-semibold text-slate-800">{f.label}{f.exclude_from_max ? <span className="ml-1 text-xs font-normal text-slate-400">(alternative — not added to max)</span> : null}</p>
                {f.type === "boolean" && (
                  <div className="mt-2 flex items-center justify-between text-sm text-slate-600">
                    <span>Points if true</span>
                    <input type="number" min={0} value={f.points} data-testid={`admin-pts-${gid}-${f.id}`}
                      onChange={(e) => setFactor(gid, f.id, (x) => ({ ...x, points: num(e.target.value) }))} className={inputCls} />
                  </div>
                )}
                {f.type === "map" && (
                  <ul className="mt-2 space-y-1.5">
                    {Object.entries(f.map).map(([k, v]) => (
                      <li key={k} className="flex items-center justify-between text-sm text-slate-600">
                        <span>{mapLabel(k, f.source)}</span>
                        <input type="number" min={0} value={v} data-testid={`admin-pts-${gid}-${f.id}-${k}`}
                          onChange={(e) => setFactor(gid, f.id, (x) => ({ ...x, map: { ...x.map, [k]: num(e.target.value) } }))} className={inputCls} />
                      </li>
                    ))}
                  </ul>
                )}
                {f.type === "band" && (
                  <ul className="mt-2 space-y-1.5">
                    {f.bands.map((b, i) => (
                      <li key={`${b.min}-${b.max ?? "max"}`} className="flex items-center justify-between text-sm text-slate-600">
                        <span>{bandLabel(b, f.source)}</span>
                        <input type="number" min={0} value={b.points} data-testid={`admin-pts-${gid}-${f.id}-${i}`}
                          onChange={(e) => setFactor(gid, f.id, (x) => ({ ...x, bands: x.bands.map((bb, j) => (j === i ? { ...bb, points: num(e.target.value) } : bb)) }))} className={inputCls} />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
